import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import AccountHomeTab from "./AccountHomeTab";
import AccountDetailsTab from "./AccountDetailsTab";
import AccountProductLog from "./AccountProductLog";
import TitleBar from "../../common/title-bar/title-bar.js";
import Button from "../../common/button/button.js";
import Text from "../../common/typography/text.js";
import APIService from "../../Service/APIService";
import { timeframeButtons } from "../Utils/Constants.js";
import { Alert, Result, Skeleton, Input } from "antd";
import TabFade from "../../common/tab-fade";
import "./AccountDetails.css";

const apiService = new APIService();

const AccountDetails = () => {
  const { id } = useParams();
  const isCreateMode = id === undefined;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // -------------------------
  // State
  // -------------------------
  const [accountDetails, setAccountDetails] = useState(null);
  const [pageState, setPageState] = useState("loading");
  const [filterButton, setFilterButton] = useState("YTD");
  const [productLogSearch, setProductLogSearch] = useState("");

  const currentTabKey = isCreateMode
    ? "details"
    : searchParams.get("tab") || "home";

  // -------------------------
  // Fetch account data
  // -------------------------
  const fetchAccount = useCallback(async () => {
    if (isCreateMode) {
      setAccountDetails(null);
      setPageState("loaded");
      return;
    }

    const parsedID = Number(id);
    if (!Number.isInteger(parsedID) || parsedID <= 0) {
      setAccountDetails(null);
      setPageState("notFound");
      return;
    }
    setPageState("loading");
    try {
      const response = await apiService.getDataFromBackend(
        `getaccountbyid?ID=${parsedID}`
      );
      setAccountDetails(response?.data?.data);
      setPageState("loaded");
    } catch (err) {
      console.error("Failed to fetch account", err);
      setAccountDetails(null);
      setPageState(
        err?.response?.status === 400 || err?.response?.status === 404
          ? "notFound"
          : "error"
      );
    }
  }, [id, isCreateMode]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  // -------------------------
  // Helpers
  // -------------------------
  const handleTabChange = (tabKey) => {
    setSearchParams({ tab: tabKey });
  };

  const isDeleted =
    accountDetails &&
    !(
      accountDetails.IsActive === true ||
      accountDetails.IsActive === 1 ||
      accountDetails.IsActive === "1"
    );

  const entityAccountName = useMemo(() => {
    if (!accountDetails) {
      return <Skeleton.Input active style={{ width: 80, height: 14 }} />;
    }
    return accountDetails.ParentAccountID
      ? `Child Account of ${accountDetails.ParentAccountName}`
      : "Account";
  }, [accountDetails]);

  const entityAccountNameLink = useMemo(() => {
    if (!accountDetails) return null;
    return accountDetails.ParentAccountID
      ? `/Account/${accountDetails.ParentAccountID}`
      : "/AccountDashboard";
  }, [accountDetails]);

  const LabelValue = useMemo(() => {
    if (!accountDetails) {
      return <Skeleton.Input active style={{ width: 260, height: 20 }} />;
    }
    return accountDetails.AccountName;
  }, [accountDetails]);

  const renderTabContent = () => {
    switch (currentTabKey) {
      case "details":
        return <AccountDetailsTab accountDetails={accountDetails} readOnly={isDeleted} />;
      case "productlog":
        return (
          <AccountProductLog readOnly={isDeleted} searchValue={productLogSearch} />
        );
      // case "consignment":
      //   return <AccountConsignmentDashboard />;
      default:
        return (
          <AccountHomeTab
            accountDetails={accountDetails}
            filterButton={filterButton}
          />
        );
    }
  };

  // -------------------------
  // Render
  // -------------------------
  if (pageState === "loading") {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (pageState === "notFound") {
    return (
      <Result
        status="404"
        title="Account Does Not Exist"
        subTitle={`No accessible account exists for ID ${id || "(missing)"}.`}
        extra={<Button onClick={() => navigate("/AccountDashboard")}>Back to Accounts</Button>}
      />
    );
  }

  if (pageState === "error") {
    return (
      <Result
        status="error"
        title="Unable to Load Account"
        subTitle="An unexpected error occurred while loading this account."
        extra={<Button onClick={fetchAccount}>Retry</Button>}
      />
    );
  }

  return (
    <div>
      {isDeleted && (
        <Alert
          type="warning"
          showIcon
          message="This account has been deleted."
          description="Deleted accounts are read-only."
          style={{ marginBottom: "16px" }}
        />
      )}
      {id &&
        <TitleBar
          label={LabelValue}
          entityName={entityAccountName}
          entityNameLink={entityAccountNameLink}
          filterBarComponents={
            <>
              <Button
                isActive={currentTabKey === "home"}
                variant="timeframe"
                onClick={() => handleTabChange("home")}
              >
                Home
              </Button>
              <Button
                isActive={currentTabKey === "details"}
                variant="timeframe"
                onClick={() => handleTabChange("details")}
              >
                Details
              </Button>
              <Button
                isActive={currentTabKey === "productlog"}
                variant="timeframe"
                onClick={() => handleTabChange("productlog")}
              >
                Product Log
              </Button>
              {/* <Button
                isActive={currentTabKey === "consignment"}
                variant="timeframe"
                onClick={() => handleTabChange("consignment")}
              >
                Consignment
              </Button> */}
            </>
          }
          rightFilterBarComponents={
            currentTabKey === "home" ? (
              <>
                <Text variant="buttonText">Timeframe</Text>
                {timeframeButtons.map((btn) => (
                  <Button
                    key={btn}
                    isActive={filterButton === btn}
                    variant="timeframe"
                    onClick={() => setFilterButton(btn)}
                  >
                    {btn}
                  </Button>
                ))}
              </>
            ) : currentTabKey === "productlog" ? (
              <Input.Search
                placeholder="Search"
                allowClear
                style={{ height: "36px" }}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setProductLogSearch("");
                  }
                }}
                onSearch={(value) => setProductLogSearch(value || "")}
              />
            ) : (
              false
            )
          }
        />
      }

      <TabFade activeKey={currentTabKey} style={{ marginTop: "20px" }}>
        {renderTabContent()}
      </TabFade>
    </div>
  );
};

export default AccountDetails;
